import React, { Fragment } from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import cn from 'classnames'
import moment from 'moment'

import { get, head, last, find } from 'lodash'
import { EventDuration, AbstractEvent } from '@scoir/react-calendar'
import { Box, Button, Chip } from '@material-ui/core'
import { Title, Section } from '../components/OverlayParts'
import { open, pending } from '../../../visit-colors'
import Icon from '@scoir/components-core/dist/components/Icon'

import sanitizeHtml from 'common/sanitize-html-helper'
import slotIcon from 'icons/filter-none.svg'
import SendChatModal from '../SendChatModal'
import * as RegisterAction from './RegisterAction'
import * as RequestAction from './RequestAction'
import * as WithdrawAction from './WithdrawAction'
import LocationSection from '../components/LocationSection'
import './index.scss'
import requiredPropTypes from './requiredPropTypes'
import { WatchLater } from '@material-ui/icons'

export { default as WeekView } from './WeekView'
export { default as MonthView } from './MonthView'

export class VisitOpening extends React.Component {
  constructor(props) {
    super(props)
    this.state = {
      showActionModal: false,
      showChatModal: false,
    }
  }
  static propTypes = {
    ...requiredPropTypes,
    scid: PropTypes.number.isRequired,
    userId: PropTypes.string.isRequired,
    summary: PropTypes.element.isRequired,
  }
  getActionName() {
    const { scid, userId, event } = this.props
    const participant = find(get(event, 'Participants', []), { Scid: scid })
    const myRep = find(get(participant, 'Representatives', []), { UserId: userId })
    const systemState = get(myRep, 'SystemState')
    const isPending = systemState === 'pending_state'
    const isConfirmed = systemState === 'confirmed_state'
    if (event.ApprovalRequired) {
      if (isPending) {
        return 'withdraw'
      }
      if (isConfirmed) {
        return 'confirmed'
      }
      return 'request'
    }
    return 'register'
  }
  getActionComponent() {
    const actions = {
      withdraw: WithdrawAction,
      register: RegisterAction,
      confirmed: RegisterAction,
      request: RequestAction,
    }
    return actions[this.getActionName()]
  }
  getStatusComponent() {
    const heading = 'Status'
    const actions = {
      withdraw: (
        <Section heading={heading}>
          <span className="status-label">
            <Chip
              color="primary"
              size="small"
              label="Pending"
              icon={<WatchLater />}
            />
          </span>
        </Section>
      ),
      confirmed: (
        <Section heading={heading}>
          <span className="status-label">
            <Chip
              color="primary"
              size="small"
              label="Confirmed"
              icon={<WatchLater />}
            />
          </span>
        </Section>
      ),
      register: false,
      request: false,
    }
    return actions[this.getActionName()]
  }
  renderDetails() {
    const { event } = this.props
    const eventTime = moment.tz(event.StartTime, event.StartTimeZone)
    const currentTime = moment.tz(moment(), event.StartTimeZone)
    const isEventInPast = eventTime.isBefore(currentTime)
    const isMultiEvent = get(event, 'Type') === 'multi'
    const participants = get(event, 'Participants', [])
    const ActionTrigger = this.getActionComponent().Trigger
    const actionName = this.getActionName()
    const virtual = event.Virtual ? 'Virtual ' : ''
    const titleProps =
      actionName === 'withdraw' ? { color: 'common.white', bgcolor: pending } : { color: 'common.white', bgcolor: open }
    return (
      <Box
        minWidth={200}
        maxWidth={400}
        className={cn('visit-opening-details', actionName)}
      >
        <Title {...titleProps}>
          <span className="details-title">
            {actionName === 'withdraw' ? (
              `Pending ${virtual}Visit Request`
            ) : (
              <Fragment>
                {isMultiEvent ? `Multiple ${virtual}Availabilities` : `${virtual}Availability`} with{' '}
                {event.HighSchoolName}
              </Fragment>
            )}
          </span>
        </Title>
        {isMultiEvent && (
          <Section>
            <Icon
              def={slotIcon}
              size="xs"
              beforeText
            />
            <span className="slots-remaining">
              {event.MaxTargetParticipants - participants.length} of {event.MaxTargetParticipants} slots remaining
            </span>
          </Section>
        )}
        <Section heading="When">
          <EventDuration
            event={event}
            isDifferentTimeZone={event.StartTimeZone !== moment.tz.guess()}
            showLocalTimeZone
          />
        </Section>
        <LocationSection
          event={event}
          registered={actionName === 'confirmed'}
        />
        <Section heading="Organizer">
          <span className="counselor-name">{event.CounselorName}</span>{' '}
          <Button
            variant="text"
            color="primary"
            size="small"
            onClick={() => this.setState({ showChatModal: true })}
          >
            Send Message
          </Button>
        </Section>
        {event.RepInstructions && (
          <Section
            maximumHeight={300}
            heading="Instructions"
          >
            <span
              className="rep-instructions"
              dangerouslySetInnerHTML={{ __html: sanitizeHtml(event.RepInstructions) }}
            />
          </Section>
        )}
        {this.getStatusComponent()}
        <Section>
          <div className="text-right">
            {!isEventInPast && <ActionTrigger onOpen={() => this.setState({ showActionModal: true })} />}
          </div>
        </Section>
      </Box>
    )
  }

  render() {
    const { className, event, summary, renderDate, onEventChange, userId } = this.props
    const ActionModal = this.getActionComponent().Modal
    return (
      <Fragment>
        <AbstractEvent
          renderDate={renderDate}
          event={event}
          summary={<div className={cn('visit-opening-comp', className)}>{summary}</div>}
          details={this.renderDetails()}
        />
        <ActionModal
          show={this.state.showActionModal}
          eventId={event.Id}
          highSchoolName={event.HighSchoolName}
          allowRepMeeting={event.Virtual && event.AllowRepLocation}
          placeName={get(event, 'Location.PlaceName')}
          onEventChange={onEventChange}
          onClose={() => this.setState({ showActionModal: false })}
        />
        <SendChatModal
          userId={userId}
          show={this.state.showChatModal}
          onClose={() => this.setState({ showChatModal: false })}
          recipient={{
            userId: event.CounselorId,
            firstName: head(event.CounselorName.split(' ')),
            lastName: last(event.CounselorName.split(' ')),
            school: event.HighSchoolName,
          }}
        />
      </Fragment>
    )
  }
}

export default connect((store) => ({
  userId: store.user.record.Id,
  scid: store.user.record.Scid,
}))(VisitOpening)
